import { StatusTag } from '@fountain/fountain-ui-components';
import { Link, Typography, useTheme } from '@material-ui/core';
import React, { useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { messages } from '../messages';
import { messages as formMessages } from '../ScorecardForm/messages';
import {
  answerDisplay,
  AnswerDisplay,
  answersByRowKey,
  ComparisonRow,
  comparisonRows,
  isPositiveRecommendation,
  NOTE_PREVIEW_LENGTH,
  notePreview,
  QUESTION_TYPE_LABELS,
  RECOMMENDATION_LABELS,
  ScorecardEvaluation,
  ScorecardResultsData,
  scoreRowMode,
} from './scorecardResults';
import { useStyles } from './styles';

const EMPTY_CELL = '-';

const NoteText: VFC<{ note: string; noteKey: string }> = ({
  note,
  noteKey,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const clamped = note.length > NOTE_PREVIEW_LENGTH;

  return (
    <>
      <Typography
        variant="caption"
        className={classes.answerNote}
        data-testid={`scorecard-note-${noteKey}`}
      >
        “{clamped && !expanded ? notePreview(note) : note}”
      </Typography>
      {clamped && (
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setExpanded(previous => !previous)}
        >
          <FormattedMessage
            {...(expanded ? messages.showLess : messages.showMore)}
          />
        </Link>
      )}
    </>
  );
};

const AnswerCellContent: VFC<{ display: AnswerDisplay; noteKey: string }> = ({
  display,
  noteKey,
}) => {
  const classes = useStyles();

  if (display.kind === 'none') {
    return (
      <Typography variant="body2" className={classes.emptyCell}>
        {EMPTY_CELL}
      </Typography>
    );
  }

  return (
    <div className={classes.answer}>
      {display.kind === 'value' && (
        <Typography variant="body2" className={classes.answerValue}>
          {display.value}
        </Typography>
      )}
      {display.kind === 'yesNo' && (
        <Typography variant="body2" className={classes.answerValue}>
          <FormattedMessage {...RECOMMENDATION_LABELS[display.value]} />
        </Typography>
      )}
      {display.note !== null && (
        <NoteText note={display.note} noteKey={noteKey} />
      )}
    </div>
  );
};

const EvaluatorHeader: VFC<{ evaluation: ScorecardEvaluation }> = ({
  evaluation,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();

  const submitted = evaluation.status === 'submitted';
  const date = submitted ? evaluation.submitted_at : evaluation.invited_at;
  const formattedDate = date
    ? intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className={classes.evaluatorHeader}>
      <Typography
        variant="body2"
        className={
          submitted ? classes.evaluatorName : classes.pendingEvaluatorName
        }
      >
        {evaluation.user.name}
      </Typography>
      {submitted && formattedDate !== null && (
        <Typography variant="caption" className={classes.submittedDate}>
          {formattedDate}
        </Typography>
      )}
      {!submitted && formattedDate !== null && (
        <Typography variant="caption" className={classes.invitedDate}>
          <FormattedMessage
            {...messages.invitedDate}
            values={{ date: formattedDate }}
          />
        </Typography>
      )}
      {evaluation.current_user && (
        <StatusTag
          className={classes.yourScorecardTag}
          status={intl.formatMessage(messages.yourScorecard)}
          color={theme.palette.common.blue600}
          backgroundColor={theme.palette.common.brandTint}
        />
      )}
    </div>
  );
};

export interface ScorecardResultsProps {
  data: ScorecardResultsData;
}

// The comparison table: one column per evaluator, one row per question, and
// a summary of each evaluator's score and recommendation at the bottom.
export const ScorecardResults: VFC<ScorecardResultsProps> = ({ data }) => {
  const classes = useStyles();
  const intl = useIntl();
  const theme = useTheme();

  const questions = comparisonRows(data);
  const answerMaps = data.evaluations.map(answersByRowKey);
  const scoreMode = scoreRowMode(data);
  const scoreRowLabel =
    scoreMode === 'weighted'
      ? formMessages.weightedTotal
      : formMessages.averageScore;

  const questionRow = (question: ComparisonRow, index: number) => (
    <tr key={question.key} className={classes.bodyRow}>
      <th scope="row" className={classes.questionCell}>
        <div className={classes.questionEntry}>
          <Typography variant="body2" className={classes.questionNumber}>
            {index + 1}.
          </Typography>
          <div className={classes.questionText}>
            <Typography variant="body2">{question.text}</Typography>
            <Typography variant="caption" className={classes.questionType}>
              <FormattedMessage
                {...QUESTION_TYPE_LABELS[question.question_type]}
              />
            </Typography>
          </div>
        </div>
      </th>
      {data.evaluations.map((evaluation, column) => (
        <td
          key={evaluation.user.id ?? column}
          className={classes.answerCell}
          data-testid={`scorecard-answer-${question.key}-${column}`}
        >
          <AnswerCellContent
            display={answerDisplay(
              question,
              answerMaps[column].get(question.key),
            )}
            noteKey={`${question.key}-${column}`}
          />
        </td>
      ))}
    </tr>
  );

  return (
    <div className={classes.scrollWrap} data-testid="scorecard-results-table">
      <table className={classes.table}>
        <thead>
          <tr className={classes.headerRow}>
            <th scope="col" className={classes.questionHeaderCell}>
              <Typography
                variant="body2"
                component="span"
                className={classes.questionColumnLabel}
              >
                <FormattedMessage {...globalMessages.question} />
              </Typography>
            </th>
            {data.evaluations.map((evaluation, column) => (
              <th
                scope="col"
                key={evaluation.user.id ?? column}
                className={classes.evaluatorHeaderCell}
              >
                <EvaluatorHeader evaluation={evaluation} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {questions.map(questionRow)}
          {scoreMode !== null && (
            <tr
              className={classes.summaryRow}
              data-testid="scorecard-score-row"
            >
              <th scope="row" className={classes.summaryLabelCell}>
                <Typography
                  variant="body2"
                  component="span"
                  className={classes.summaryLabel}
                >
                  <FormattedMessage {...scoreRowLabel} />
                </Typography>
              </th>
              {data.evaluations.map((evaluation, column) => (
                <td
                  key={evaluation.user.id ?? column}
                  className={classes.summaryValueCell}
                >
                  {evaluation.status === 'submitted' &&
                  evaluation.score !== null ? (
                    <Typography
                      variant="body2"
                      component="span"
                      className={classes.summaryScore}
                    >
                      {evaluation.score.toFixed(2)}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      component="span"
                      className={classes.emptyCell}
                    >
                      {EMPTY_CELL}
                    </Typography>
                  )}
                </td>
              ))}
            </tr>
          )}
          <tr
            className={classes.summaryRow}
            data-testid="scorecard-recommendation-row"
          >
            <th scope="row" className={classes.summaryLabelCell}>
              <Typography
                variant="body2"
                component="span"
                className={classes.summaryLabel}
              >
                <FormattedMessage {...QUESTION_TYPE_LABELS.recommendation} />
              </Typography>
            </th>
            {data.evaluations.map((evaluation, column) => (
              <td
                key={evaluation.user.id ?? column}
                className={classes.summaryValueCell}
              >
                {evaluation.recommendation ? (
                  <StatusTag
                    className={classes.recommendationTag}
                    status={intl.formatMessage(
                      RECOMMENDATION_LABELS[evaluation.recommendation],
                    )}
                    color={
                      // green800: green600 on green100 fails WCAG AA (3.12:1).
                      isPositiveRecommendation(evaluation.recommendation)
                        ? theme.palette.common.green800
                        : theme.palette.common.red600
                    }
                    backgroundColor={
                      isPositiveRecommendation(evaluation.recommendation)
                        ? theme.palette.common.green100
                        : theme.palette.common.red100
                    }
                  />
                ) : (
                  <Typography
                    variant="body2"
                    component="span"
                    className={classes.emptyCell}
                  >
                    {EMPTY_CELL}
                  </Typography>
                )}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
